var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
var __decorateParam = (index, decorator) => (target, key) => decorator(target, key, index);

// api/buchung.ts
import { Float } from "@adornis/baseql/baseqlTypes.js";
import { Arg, Entity, Field, Mutation } from "@adornis/baseql/decorators.js";
import { DataLoadedField } from "@adornis/baseql/decorators/data-loaded-field.js";
import { AdornisEntity } from "@adornis/baseql/entities/adornisEntity.js";
import { MongoEntity } from "@adornis/baseql/entities/mongoEntity.js";
import { getRawCollection } from "@adornis/baseql/server/collections.js";
import { loadMongoData } from "@adornis/baseql/server/data-loader.js";
import { validate } from "@adornis/validation/decorators.js";
import { ValidationError } from "@adornis/validation/errors/ValidationError.js";
import { and } from "@adornis/validation/functions/and.js";
import { nonOptional } from "@adornis/validation/functions/nonOptional.js";
import { DateTime } from "luxon";
import { CurrencyAmount } from "./currency-amount.js";
import { SKR04 } from "./kontenrahmen.js";
import { Zusammenhangskomponente } from "./zusammenhangskomponente.js";
var Kostenstellenzuweisung = class extends AdornisEntity {
  constructor(doc) {
    super(doc);
  }
  static resolveID(entity) {
    return entity.kostenstelle;
  }
};
Kostenstellenzuweisung._class = "Kostenstellenzuweisung";
__decorateClass([
  Field((type) => String),
  validate(nonOptional())
], Kostenstellenzuweisung.prototype, "kostenstelle", 2);
__decorateClass([
  Field((type) => Float, { default: (v) => v ?? 1 })
], Kostenstellenzuweisung.prototype, "anteilig", 2);
Kostenstellenzuweisung = __decorateClass([
  Entity()
], Kostenstellenzuweisung);
var Buchung = class extends MongoEntity {
  constructor(doc) {
    super(doc);
  }
  static async transformBeforeUpdate(oldEntity, update) {
    await super.transformBeforeUpdate(oldEntity, update);
    if (oldEntity.committed && ["sollKonto", "habenKonto", "betrag", "buchungsDatum"].some((key) => Object.keys(update).includes(key)))
      throw new Error(`Can not change committed Buchung ${oldEntity._id}`);
  }
  static async beforeCreate(entity) {
    if (entity.betrag.betrag < 0)
      throw new Error("cant create Buchung with negative amount, doesnt make sense in accounting");
  }
  static async beforeRemove(id, entityData) {
    await super.beforeRemove(id, entityData);
    if (entityData.committed)
      throw new Error("cant remove committed Buchung");
  }
  static erstelleNachtragsBuchung(buchungsReferenz, buchung) {
    throw new Error("not implemented");
    return async () => new Buchung({});
  }
  static commitBuchungen(buchungsIDs) {
    return async () => {
      const buchungen = await loadMongoData(this, buchungsIDs);
      if (!buchungsIDs.every((nr) => buchungen.some((b) => b._id === nr)))
        throw new Error("not all buchungsIDs could be found");
      const buchungsColl = await getRawCollection(this._class);
      await buchungsColl.updateMany({ _id: { $in: buchungsIDs } }, { $set: { committed: true } });
      return true;
    };
  }
  /**
   * Leistung an Kunden
   *
   * @param debitorenKonto Zahlender
   * @param kreditorenKonto empfangendes Konto (Umsatzkonto)
   * @param buchung restliche Attribute einer Buchung
   * @returns die Buchung
   */
  static erstelleDebitorenForderung(debitorenKonto, kreditorenKonto, buchung) {
    return new this({ ...buchung, sollKonto: debitorenKonto, habenKonto: kreditorenKonto });
  }
  /**
   * Leistung zB eines Lieferanten oder so Sachen wie Kaution.
   *
   * @param debitorenKonto leistendes Konto
   * @param kreditorenKonto Empfänger
   * @param buchung restliche Attribute einer Buchung
   * @returns die Buchung
   */
  static erstelleKreditorenForderung(debitorenKonto, kreditorenKonto, buchung) {
    return new this({ ...buchung, sollKonto: kreditorenKonto, habenKonto: debitorenKonto });
  }
  /**
   * Invertiert soll- und haben-konto der Forderung
   */
  // TODO typing shouldnt be T both times
  // TODO in Morten, deposits automatically link themselves to Forderungen via Zusammenhangskomponenten. We should automate this here
  static bucheZahlung(forderung, zahlung) {
    return new this({ ...zahlung, habenKonto: forderung.sollKonto, sollKonto: forderung.habenKonto });
  }
  toString() {
    return `${this.buchungsText} (${this.buchungsDatum.toLocaleString(
      DateTime.DATE_SHORT
    )}) ${this.betrag.toString()} ${this.sollKonto} an ${this.habenKonto}`;
  }
};
Buchung._class = "Buchung";
Buchung._collectionName = "buchungen";
Buchung.kontenRahmen = SKR04;
__decorateClass([
  Field((type) => String)
], Buchung.prototype, "belegID", 2);
__decorateClass([
  Field((type) => String),
  validate(nonOptional())
], Buchung.prototype, "sollKonto", 2);
__decorateClass([
  Field((type) => String),
  validate(nonOptional())
], Buchung.prototype, "habenKonto", 2);
__decorateClass([
  Field((type) => CurrencyAmount),
  validate(
    and(nonOptional(), ({ value, target, key }) => {
      if (value < 0)
        throw new ValidationError("betrag kann nicht negativ sein", { target, key });
    })
  )
], Buchung.prototype, "betrag", 2);
__decorateClass([
  Field((type) => DateTime, { default: (v) => v ?? DateTime.now() }),
  validate(nonOptional())
], Buchung.prototype, "buchungsDatum", 2);
__decorateClass([
  Field((type) => String, {
    resolve() {
      return async () => {
        return "buchungsText not implemented";
      };
    }
  })
], Buchung.prototype, "buchungsText", 2);
__decorateClass([
  Field((type) => Boolean, { default: (v) => v ?? true })
], Buchung.prototype, "committed", 2);
__decorateClass([
  Field((type) => String)
], Buchung.prototype, "buchungsReferenz", 2);
__decorateClass([
  DataLoadedField(() => [Zusammenhangskomponente], ({ _id }) => ({ buchungsIDs: _id }))
], Buchung.prototype, "zusammenhaenge", 2);
__decorateClass([
  validate(({ value, target, key }) => {
    if (value?.reduce((acc, curr) => acc + (curr.anteilig ?? 1), 0) !== 1)
      throw new ValidationError("Kostenstellen dont add up", { target, key });
  }),
  Field((type) => [Kostenstellenzuweisung])
], Buchung.prototype, "kostenstellen", 2);
__decorateClass([
  Mutation((type) => Boolean),
  __decorateParam(0, Arg("buchungsReferenz", (type) => String)),
  __decorateParam(1, Arg("buchung", (type) => Buchung))
], Buchung, "erstelleNachtragsBuchung", 1);
__decorateClass([
  Mutation((type) => Boolean),
  __decorateParam(0, Arg("buchungsIDs", (type) => [String]))
], Buchung, "commitBuchungen", 1);
Buchung = __decorateClass([
  Entity()
], Buchung);
export {
  Buchung,
  Kostenstellenzuweisung
};
